import React, { useCallback, useEffect, useState } from "react";

import { CButton, CInput, CPagination, CSpinner, CSwitch } from "@coreui/react";
import {
  FaArrowLeft,
  FaBookOpen,
  FaFilter,
  FaPencilAlt,
  FaPlusCircle,
  FaSearch,
  FaTimes
} from "react-icons/fa";
import { toastr } from "react-redux-toastr";

import FormularioRichContent, {
  formularioContentHasValue
} from "./FormularioRichContent";
import { MAX_OPCOES, MIN_OPCOES, letraDaOpcao } from "./opcoes";
import URL from "../../config/api";
import axios from "../../config/axios";

import "./formulario-editor.css";

const auth = token => ({ headers: { authorization: `Bearer ${token}` } });
const EMPTY_DISCIPLINES = [];

/* Cada alternativa tem o seu editor Slate, que guarda seleção própria. Com
   chave pelo índice, remover a do meio entregaria o texto da vizinha a um
   editor com seleção antiga — daí a chave local, que não vai para a API. */
let proximaChave = 0;
const novaOpcao = (texto = "") => ({ chave: (proximaChave += 1), texto });

const emptyQuestion = () => ({
  enunciado: "",
  disciplina_id: "",
  visibilidade: "privado",
  opcoes: Array.from({ length: MIN_OPCOES }, () => novaOpcao())
});

export function QuestionForm({
  token,
  disciplines = EMPTY_DISCIPLINES,
  question = null,
  onCancel,
  onSaved
}) {
  const [availableDisciplines, setAvailableDisciplines] = useState(disciplines);
  const [form, setForm] = useState(() =>
    question
      ? {
          enunciado: question.enunciado,
          disciplina_id: question.disciplina_id || "",
          visibilidade: question.visibilidade || "privado",
          opcoes: (question.opcoes || []).map(option => novaOpcao(option.texto))
        }
      : emptyQuestion()
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (disciplines.length) {
      setAvailableDisciplines(disciplines);
      return;
    }
    axios
      .get(`${URL.api}classification/discipline/list`, auth(token))
      .then(response => setAvailableDisciplines(response.data?.data || []))
      .catch(() => setAvailableDisciplines([]));
  }, [disciplines, token]);

  const save = async event => {
    event.preventDefault();
    if (
      !formularioContentHasValue(form.enunciado) ||
      form.opcoes.some(option => !formularioContentHasValue(option.texto))
    ) {
      toastr.error("Atenção", "Preencha o enunciado e todas as alternativas.");
      return;
    }
    if (form.opcoes.length < MIN_OPCOES || form.opcoes.length > MAX_OPCOES) {
      toastr.error(
        "Atenção",
        `A pergunta precisa ter de ${MIN_OPCOES} a ${MAX_OPCOES} alternativas.`
      );
      return;
    }
    setSaving(true);
    try {
      const response = await axios({
        method: question ? "patch" : "post",
        url: `${URL.api}formulario/questions${
          question ? `/${question.id}` : ""
        }`,
        data: {
          ...form,
          disciplina_id: form.disciplina_id || null,
          opcoes: form.opcoes.map(({ texto }) => ({ texto }))
        },
        ...auth(token)
      });
      onSaved(response.data.data);
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível salvar a pergunta."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="formulario-question-create" onSubmit={save}>
      <div className="formulario-bank-toolbar">
        <div>
          <h3>{question ? "Editar pergunta" : "Nova pergunta"}</h3>
          <p>
            {question
              ? "Atualize o conteúdo da pergunta selecionada."
              : "Cadastre a pergunta uma vez e reutilize-a em outros formulários."}
          </p>
        </div>
        <CButton
          type="button"
          color="secondary"
          variant="outline"
          onClick={onCancel}
        >
          <FaArrowLeft className="mr-2" /> Voltar ao banco
        </CButton>
      </div>
      <div className="formulario-question-create__body">
        <div className="formulario-question-create__form">
          <div className="formulario-question-create__field">
            <label htmlFor="formulario-question-statement">Enunciado</label>
            <FormularioRichContent
              token={token}
              placeholder="Digite o enunciado da pergunta"
              value={form.enunciado}
              onChange={enunciado => setForm({ ...form, enunciado })}
            />
          </div>
          <div className="formulario-question-create__metadata">
            <div className="formulario-question-create__field formulario-question-create__discipline">
              <label htmlFor="formulario-question-discipline">
                Componente curricular
              </label>
              <select
                id="formulario-question-discipline"
                className="form-control"
                value={form.disciplina_id}
                onChange={event =>
                  setForm({ ...form, disciplina_id: event.target.value })
                }
              >
                <option value="">Sem componente curricular</option>
                {availableDisciplines.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="formulario-question-create__visibility">
              <CSwitch
                shape="pill"
                color="primary"
                size="sm"
                checked={form.visibilidade === "publico"}
                onChange={event =>
                  setForm({
                    ...form,
                    visibilidade: event.target.checked ? "publico" : "privado"
                  })
                }
              />
              <div>
                <strong>Compartilhar com todos os usuários</strong>
                <small>
                  {form.visibilidade === "publico"
                    ? "Pergunta pública"
                    : "Pergunta privada"}
                </small>
              </div>
            </div>
          </div>

          <div className="formulario-question-create__alternatives-heading">
            <strong>Alternativas</strong>
            <small>
              De {MIN_OPCOES} a {MAX_OPCOES} alternativas, sem resposta correta.
            </small>
          </div>
          <div className="formulario-question-create__options">
            {form.opcoes.map((option, index) => (
              <div
                className="formulario-question-create__option"
                key={option.chave}
              >
                <span className="formulario-question-create__letter">
                  {letraDaOpcao(index)}
                </span>
                <FormularioRichContent
                  token={token}
                  placeholder=""
                  value={option.texto}
                  onChange={texto =>
                    setForm(old => ({
                      ...old,
                      opcoes: old.opcoes.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, texto } : item
                      )
                    }))
                  }
                />
                {form.opcoes.length > MIN_OPCOES && (
                  <button
                    type="button"
                    className="formulario-question-create__remove-option"
                    title="Remover alternativa"
                    aria-label={`Remover alternativa ${letraDaOpcao(index)}`}
                    onClick={() =>
                      setForm(old => ({
                        ...old,
                        opcoes: old.opcoes.filter(
                          (_, itemIndex) => itemIndex !== index
                        )
                      }))
                    }
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {form.opcoes.length < MAX_OPCOES && (
            <CButton
              type="button"
              color="primary"
              variant="outline"
              size="sm"
              className="formulario-question-create__add-option"
              onClick={() =>
                setForm(old => ({
                  ...old,
                  opcoes: [...old.opcoes, novaOpcao()]
                }))
              }
            >
              <FaPlusCircle className="mr-2" aria-hidden="true" /> Adicionar
              alternativa
            </CButton>
          )}
        </div>
      </div>
      <footer className="formulario-question-create__actions">
        <span>Todos os campos de alternativa são obrigatórios.</span>
        <div>
          <CButton
            type="button"
            color="secondary"
            variant="outline"
            className="mr-2"
            onClick={onCancel}
          >
            Cancelar
          </CButton>
          <CButton type="submit" color="primary" disabled={saving}>
            {saving ? (
              <CSpinner size="sm" />
            ) : question ? (
              "Salvar alterações"
            ) : (
              "Salvar pergunta"
            )}
          </CButton>
        </div>
      </footer>
    </form>
  );
}

export function FormularioQuestionPreview({
  question,
  selected,
  onToggle,
  allowSelection,
  onEdit
}) {
  return (
    <article
      className={`formulario-bank-question ${selected ? "is-selected" : ""}`}
    >
      <div className="formulario-bank-question__header">
        <small>
          {question.disciplina || "Sem disciplina"} · {question.autor || "Você"}
        </small>
        {question.pode_editar && (
          <CButton
            type="button"
            color="primary"
            variant="ghost"
            size="sm"
            title="Editar pergunta"
            aria-label="Editar pergunta"
            onClick={() => onEdit?.(question)}
          >
            <FaPencilAlt aria-hidden="true" />
          </CButton>
        )}
      </div>
      <div className="formulario-bank-question__statement">
        <FormularioRichContent value={question.enunciado} readOnly />
      </div>
      <div className="formulario-bank-question__options">
        {(question.opcoes || []).map((option, index) => (
          <div key={option.id || index}>
            <span className="formulario-bank-question__radio" />
            <span>{letraDaOpcao(index)}</span>
            <FormularioRichContent value={option.texto} readOnly />
          </div>
        ))}
      </div>
      <footer className="formulario-bank-question__footer">
        <span className="formulario-bank-question__count">
          {(question.opcoes || []).length} alternativa(s)
        </span>
        {allowSelection && (
          <label className="formulario-bank-question__select">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggle?.(question)}
            />
            Selecionar pergunta
          </label>
        )}
      </footer>
    </article>
  );
}

export default function FormularioQuestionBank({
  token,
  selectedIds = [],
  onToggle,
  allowSelection = false,
  onCreatingChange
}) {
  const [questions, setQuestions] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [search, setSearch] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [origin, setOrigin] = useState("todas");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${URL.api}formulario/questions`, {
        ...auth(token),
        params: {
          per_page: 1,
          page,
          search: search || undefined,
          disciplina_id: discipline || undefined,
          origem: origin === "todas" ? undefined : origin
        }
      });
      setQuestions(response.data?.data?.data || []);
      setLastPage(response.data?.data?.last_page || 1);
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível carregar as perguntas."
      );
    } finally {
      setLoading(false);
    }
  }, [token, page, search, discipline, origin]);

  useEffect(() => {
    axios
      .get(`${URL.api}classification/discipline/list`, auth(token))
      .then(response => setDisciplines(response.data?.data || []))
      .catch(() => setDisciplines([]));
  }, [token]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    onCreatingChange?.(creating || Boolean(editingQuestion));
  }, [creating, editingQuestion, onCreatingChange]);

  if (editingQuestion)
    return (
      <QuestionForm
        token={token}
        disciplines={disciplines}
        question={editingQuestion}
        onCancel={() => setEditingQuestion(null)}
        onSaved={question => {
          setEditingQuestion(null);
          setQuestions(old =>
            old.map(item => (item.id === question.id ? question : item))
          );
        }}
      />
    );

  if (creating)
    return (
      <QuestionForm
        token={token}
        disciplines={disciplines}
        onCancel={() => setCreating(false)}
        onSaved={question => {
          setCreating(false);
          setQuestions([question]);
          if (allowSelection) onToggle?.(question);
        }}
      />
    );

  return (
    <div className="formulario-bank">
      <div className="formulario-bank__filters">
        <div className="formulario-bank__filters-heading">
          <FaFilter aria-hidden="true" />
          <span>Filtros</span>
        </div>
        <div className="formulario-bank__filter">
          <label>Componente curricular</label>
          <select
            className="form-control"
            value={discipline}
            onChange={event => {
              setDiscipline(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            {disciplines.map(item => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <div className="formulario-bank__filter">
          <label>Origem</label>
          <select
            className="form-control"
            value={origin}
            onChange={event => {
              setOrigin(event.target.value);
              setPage(1);
            }}
          >
            <option value="todas">Minhas e públicas</option>
            <option value="minhas">Somente minhas</option>
            <option value="publicas">Somente públicas</option>
          </select>
        </div>
        <div className="formulario-bank__filter formulario-bank__filter--search">
          <label>Pesquisar</label>
          <div className="formulario-bank__search">
            <FaSearch aria-hidden="true" />
            <CInput
              placeholder="Enunciado da pergunta"
              value={search}
              onChange={event => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
        <CButton
          className="formulario-bank__new-question"
          color="primary"
          onClick={() => setCreating(true)}
        >
          <FaPlusCircle className="mr-2" /> Nova pergunta
        </CButton>
      </div>
      <main className="formulario-bank__content">
        <div className="formulario-bank-toolbar">
          <div>
            <h3>Perguntas disponíveis</h3>
            <p>
              {allowSelection
                ? `${selectedIds.length} pergunta(s) selecionada(s)`
                : "Perguntas próprias e compartilhadas por outros professores"}
            </p>
          </div>
        </div>
        {loading ? (
          <div className="formulario-bank__empty">
            <CSpinner />
          </div>
        ) : questions.length ? (
          questions.map(question => (
            <FormularioQuestionPreview
              key={question.id}
              question={question}
              selected={selectedIds.includes(question.id)}
              onToggle={allowSelection ? onToggle : undefined}
              allowSelection={allowSelection}
              onEdit={setEditingQuestion}
            />
          ))
        ) : (
          <div className="formulario-bank__empty">
            <span className="formulario-bank__empty-icon">
              <FaBookOpen aria-hidden="true" />
            </span>
            <strong>Nenhuma pergunta encontrada</strong>
            <p>Ajuste os filtros ou cadastre uma nova pergunta para começar.</p>
            <CButton
              color="primary"
              variant="outline"
              onClick={() => setCreating(true)}
            >
              <FaPlusCircle className="mr-2" /> Criar pergunta
            </CButton>
          </div>
        )}
        <div className="formulario-bank__pagination">
          {lastPage > 1 && (
            <CPagination
              activePage={page}
              pages={lastPage}
              onActivePageChange={setPage}
            />
          )}
        </div>
      </main>
    </div>
  );
}
